import { createContext, useContext, useEffect, useRef, useState, ReactNode, useCallback } from 'react'
import { useTreeStore } from '../../store/useTreeStore'
import { HAND_TRACKING_CONFIG } from '../../utils/constants'
import { mapRange, clamp } from '../../utils/positions'

interface HandGestureContextType {
  isTracking: boolean
  isLoading: boolean
  isHandOpen: boolean
  handCount: number
  startTracking: () => void
  stopTracking: () => void
  videoRef: React.RefObject<HTMLVideoElement | null>
  canvasRef: React.RefObject<HTMLCanvasElement | null>
}

const HandGestureContext = createContext<HandGestureContextType>({
  isTracking: false,
  isLoading: false,
  isHandOpen: false,
  handCount: 0,
  startTracking: () => {},
  stopTracking: () => {},
  videoRef: { current: null },
  canvasRef: { current: null },
})

export const useHandGesture = () => useContext(HandGestureContext)

interface HandGestureProviderProps {
  children: ReactNode
}

export function HandGestureProvider({ children }: HandGestureProviderProps) {
  const [isTracking, setIsTracking] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const handsRef = useRef<any>(null)
  const cameraRef = useRef<any>(null)
  const animationFrameRef = useRef<number | null>(null)
  const lastHandXRef = useRef<number | null>(null)
  const lastHandsXRef = useRef<number[]>([]) // 多手追踪
  const hasInitializedRef = useRef(false)
  const gallerySwipeAccumulatorRef = useRef<number>(0) // 画廊模式滑动累积器
  const lastPhotoSwitchTimeRef = useRef<number>(0) // 上次切换照片的时间
  
  const {
    setIsHandTracking,
    setCameraRotation,
    setTreeRotationY,
    setSceneRotationY,
    nextPhoto,
    prevPhoto,
    updateFromGesture,
    isHandOpen,
    handCount
  } = useTreeStore()
  
  // 检测手是否张开 - 优化版本，提高灵敏度
  const detectHandOpen = (landmarks: any[]) => {
    if (!landmarks || landmarks.length < 21) return false
    
    // 手指尖端索引: 拇指4, 食指8, 中指12, 无名指16, 小指20
    // 手指中间关节索引: 拇指3, 食指7, 中指11, 无名指15, 小指19
    // 手指根部索引: 拇指2, 食指5, 中指9, 无名指13, 小指17
    
    const fingerTips = [4, 8, 12, 16, 20]
    const fingerMiddles = [3, 7, 11, 15, 19]
    const fingerBases = [2, 5, 9, 13, 17]
    
    let extendedFingers = 0
    
    for (let i = 0; i < 5; i++) {
      const tip = landmarks[fingerTips[i]]
      const middle = landmarks[fingerMiddles[i]]
      const base = landmarks[fingerBases[i]]
      
      // 如果指尖比根部更远离手掌中心，则认为手指伸展
      if (i === 0) {
        // 拇指特殊处理 - 检查x方向，使用更宽松的阈值
        const tipDist = Math.abs(tip.x - landmarks[0].x)
        const baseDist = Math.abs(base.x - landmarks[0].x)
        if (tipDist > baseDist * 0.8) { // 降低阈值，更容易检测
          extendedFingers++
        }
      } else {
        // 其他手指 - 检查y方向，使用中间关节辅助判断
        // 指尖应该比中间关节更高（y值更小）
        const tipToMiddle = middle.y - tip.y
        const middleToBase = base.y - middle.y
        
        // 如果指尖高于中间关节，或者整体趋势是伸展的
        if (tip.y < middle.y || (tipToMiddle > 0 && middleToBase > 0)) {
          extendedFingers++
        }
      }
    }
    
    // 如果3个或更多手指伸展，认为手是张开的（降低阈值提高灵敏度）
    return extendedFingers >= 3
  }
  
  // 检测是否为移动设备
  const isMobileDevice = () => {
    if (typeof window === 'undefined') return false
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768
  }
  
  // 从手掌位置更新摄像机旋转和场景旋转
  const updateFromHandMovement = (allLandmarks: any[][], currentTargetState: string) => {
    if (!allLandmarks || allLandmarks.length === 0) return
    
    // 使用第一只手的手掌中心
    const landmarks = allLandmarks[0]
    if (!landmarks || landmarks.length < 21) return
    
    const palmCenter = landmarks[9]
    const isMobile = isMobileDevice()
    
    // 映射到摄像机旋转范围（上下视角）- 移动端使用更大的范围
    const rotationX = mapRange(palmCenter.y, 0.2, 0.8, isMobile ? 40 : 30, isMobile ? -40 : -30)
    
    setCameraRotation({
      x: clamp(rotationX, -40, 40),
      y: 0,
    })
    
    // 根据当前模式决定操作
    const currentX = palmCenter.x
    if (lastHandXRef.current !== null) {
      const deltaX = currentX - lastHandXRef.current
      
      // 移动端使用更高的灵敏度
      const rotationMultiplier = isMobile ? 300 : 200
      
      if (currentTargetState === 'FORMED') {
        // 圣诞树模式：旋转圣诞树
        const rotationDelta = deltaX * rotationMultiplier
        setTreeRotationY(rotationDelta)
      } else if (currentTargetState === 'CHAOS') {
        // 展开模式：拨动整个场景
        const rotationDelta = deltaX * rotationMultiplier
        setSceneRotationY(rotationDelta)
      } else if (currentTargetState === 'GALLERY') {
        // 画廊模式：左右滑动切换照片（不旋转）
        gallerySwipeAccumulatorRef.current += deltaX
        
        const now = Date.now()
        const timeSinceLastSwitch = now - lastPhotoSwitchTimeRef.current
        // 移动端使用更低的阈值和更短的冷却时间
        const swipeThreshold = isMobile ? 0.10 : 0.15
        const cooldownTime = isMobile ? 350 : 500
        
        if (timeSinceLastSwitch > cooldownTime) {
          if (gallerySwipeAccumulatorRef.current > swipeThreshold) {
            // 向右滑动 -> 上一张照片
            prevPhoto()
            gallerySwipeAccumulatorRef.current = 0
            lastPhotoSwitchTimeRef.current = now
          } else if (gallerySwipeAccumulatorRef.current < -swipeThreshold) {
            // 向左滑动 -> 下一张照片
            nextPhoto()
            gallerySwipeAccumulatorRef.current = 0
            lastPhotoSwitchTimeRef.current = now
          }
        }
      }
    }
    lastHandXRef.current = currentX
  }
  
  // 当手离开画面时重置
  const resetHandTracking = () => {
    lastHandXRef.current = null
    lastHandsXRef.current = []
    gallerySwipeAccumulatorRef.current = 0
  }
  
  // 绘制手部骨架
  const drawHandLandmarks = useCallback((canvas: HTMLCanvasElement, video: HTMLVideoElement, landmarks: any[]) => {
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    
    // 设置画布尺寸匹配视频
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    
    // 清除画布
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    
    // 绘制视频帧
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    
    if (!landmarks || landmarks.length < 21) return
    
    // 手指连接关系
    const connections = [
      // 拇指
      [0, 1], [1, 2], [2, 3], [3, 4],
      // 食指
      [0, 5], [5, 6], [6, 7], [7, 8],
      // 中指
      [0, 9], [9, 10], [10, 11], [11, 12],
      // 无名指
      [0, 13], [13, 14], [14, 15], [15, 16],
      // 小指
      [0, 17], [17, 18], [18, 19], [19, 20],
      // 手掌
      [5, 9], [9, 13], [13, 17]
    ]
    
    // 绘制连接线
    ctx.strokeStyle = '#00ff00'
    ctx.lineWidth = 3
    connections.forEach(([start, end]) => {
      const startPoint = landmarks[start]
      const endPoint = landmarks[end]
      ctx.beginPath()
      ctx.moveTo(startPoint.x * canvas.width, startPoint.y * canvas.height)
      ctx.lineTo(endPoint.x * canvas.width, endPoint.y * canvas.height)
      ctx.stroke()
    })
    
    // 绘制关节点
    landmarks.forEach((point, index) => {
      const x = point.x * canvas.width
      const y = point.y * canvas.height
      
      // 指尖用红色，其他用绿色
      const isTip = [4, 8, 12, 16, 20].includes(index)
      ctx.fillStyle = isTip ? '#ff0000' : '#00ff00'
      
      ctx.beginPath()
      ctx.arc(x, y, isTip ? 8 : 5, 0, Math.PI * 2)
      ctx.fill()
      
      // 添加白色边框
      ctx.strokeStyle = '#ffffff'
      ctx.lineWidth = 2
      ctx.stroke()
    })
  }, [])
  
  // 初始化手势检测
  const initHandTracking = async () => {
    if (isLoading || isTracking) return
    
    setIsLoading(true)
    
    try {
      // 动态导入 MediaPipe
      const { Hands } = await import('@mediapipe/hands')
      const { Camera } = await import('@mediapipe/camera_utils')
      
      // 创建视频元素（不再隐藏，用于预览）
      const video = document.createElement('video')
      video.id = 'hand-tracking-video'
      video.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none;width:1px;height:1px;'
      document.body.appendChild(video)
      videoRef.current = video
      
      // 创建画布用于显示手势追踪结果
      const canvas = document.createElement('canvas')
      canvas.id = 'hand-tracking-canvas'
      canvas.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none;width:1px;height:1px;'
      document.body.appendChild(canvas)
      canvasRef.current = canvas
      
      // 初始化 Hands
      const hands = new Hands({
        locateFile: (file: string) => {
          return `https://cdn.jsdelivr.net/npm/@mediapipe/hands/${file}`
        },
      })
      
      hands.setOptions({
        maxNumHands: 2, // 支持双手检测
        modelComplexity: HAND_TRACKING_CONFIG.modelComplexity,
        minDetectionConfidence: HAND_TRACKING_CONFIG.minDetectionConfidence,
        minTrackingConfidence: HAND_TRACKING_CONFIG.minTrackingConfidence,
      })
      
      hands.onResults((results: any) => {
        // 绘制所有手的骨架到画布
        if (canvasRef.current && videoRef.current) {
          const ctx = canvasRef.current.getContext('2d')
          if (ctx) {
            canvasRef.current.width = videoRef.current.videoWidth
            canvasRef.current.height = videoRef.current.videoHeight
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
            ctx.drawImage(videoRef.current, 0, 0)
            
            // 绘制所有检测到的手
            if (results.multiHandLandmarks) {
              results.multiHandLandmarks.forEach((landmarks: any[]) => {
                drawHandLandmarks(canvasRef.current!, videoRef.current!, landmarks)
              })
            }
          }
        }
        
        const detectedHandCount = results.multiHandLandmarks?.length || 0
        
        if (detectedHandCount > 0) {
          // 检测每只手是否张开
          const handsOpen: boolean[] = results.multiHandLandmarks.map((landmarks: any[]) =>
            detectHandOpen(landmarks)
          )
          
          // 更新手势状态
          const currentTargetState = useTreeStore.getState().targetState
          updateFromGesture(detectedHandCount, handsOpen)
          
          // 更新摄像机和场景旋转
          updateFromHandMovement(results.multiHandLandmarks, currentTargetState)
        } else {
          // 没有检测到手，重置追踪
          updateFromGesture(0, [])
          resetHandTracking()
        }
      })
      
      handsRef.current = hands
      
      // 初始化摄像头
      const camera = new Camera(video, {
        onFrame: async () => {
          if (handsRef.current) {
            await handsRef.current.send({ image: video })
          }
        },
        width: 640,
        height: 480,
      })
      
      cameraRef.current = camera
      await camera.start()
      
      setIsTracking(true)
      setIsHandTracking(true)
      setIsLoading(false)
      
    } catch (error) {
      console.error('手势检测初始化失败:', error)
      setIsTracking(false)
      setIsHandTracking(false)
      setIsLoading(false)
    }
  }
  
  // 停止手势检测
  const stopHandTracking = () => {
    if (cameraRef.current) {
      cameraRef.current.stop()
      cameraRef.current = null
    }
    
    if (handsRef.current) {
      handsRef.current.close()
      handsRef.current = null
    }
    
    if (videoRef.current) {
      videoRef.current.remove()
      videoRef.current = null
    }
    
    if (canvasRef.current) {
      canvasRef.current.remove()
      canvasRef.current = null
    }
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    
    setIsTracking(false)
    setIsHandTracking(false)
  }
  
  // 不再自动启动手势控制 - 改为手动启动
  // 用户点击按钮后才启动摄像头和手势追踪
  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true
      
      // 移动端不需要手势控制
      if (isMobileDevice()) {
        console.log('移动端设备，跳过手势控制初始化')
        return
      }
      
      // 电脑端也不自动启动，等待用户手动开启
      console.log('电脑端设备，等待用户手动开启手势控制')
    }
  }, [])
  
  // 清理
  useEffect(() => {
    return () => {
      stopHandTracking()
    }
  }, [])
  
  const contextValue: HandGestureContextType = {
    isTracking,
    isLoading,
    isHandOpen,
    handCount,
    startTracking: initHandTracking,
    stopTracking: stopHandTracking,
    videoRef,
    canvasRef,
  }
  
  return (
    <HandGestureContext.Provider value={contextValue}>
      {children}
    </HandGestureContext.Provider>
  )
}